import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Users, ShieldCheck, Clock, CreditCard, Boxes, CalendarCheck, CalendarClock, Flag, HeartHandshake, MapPin, ArrowRight, RefreshCw } from "lucide-react";

const CARDS = [
    { key: "active_members", label: "Active Members", icon: Users, to: "/admin/members" },
    { key: "verified_members", label: "Verified Members", icon: ShieldCheck, to: "/admin/members" },
    { key: "pending_verifications", label: "Pending Verifications", icon: Clock, to: "/admin/members", tone: "amber" },
    { key: "active_subscriptions", label: "Active Subscriptions", icon: CreditCard, to: "/admin/members" },
    { key: "groups_forming", label: "Groups Forming", icon: Boxes, to: "/admin/groups" },
    { key: "confirmed_outings", label: "Confirmed Outings", icon: CalendarCheck, to: "/admin/outings" },
    { key: "upcoming_outings", label: "Upcoming Outings", icon: CalendarClock, to: "/admin/outings" },
    { key: "reports_pending", label: "Reports to Review", icon: Flag, to: "/admin/reports", tone: "red" },
    { key: "new_mutual_interests", label: "New Mutual Interests", icon: HeartHandshake, to: "/admin/mutual-interest", tone: "blue" },
];

export default function Dashboard() {
    const qc = useQueryClient();
    const [refreshedAt, setRefreshedAt] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const refresh = async () => {
        setRefreshing(true);
        await qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith("admin-") });
        setRefreshedAt(new Date());
        setRefreshing(false);
    };
    const { data } = useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await api.get("/admin/stats")).data });
    const { data: outings } = useQuery({ queryKey: ["admin-outings"], queryFn: async () => (await api.get("/admin/outings")).data });
    const { data: cities } = useQuery({ queryKey: ["admin-city-stats"], queryFn: async () => (await api.get("/admin/stats/cities")).data });

    const upcoming = (outings || [])
        .filter((o) => ["confirmed", "in_progress"].includes(o.status))
        .sort((a, b) => `${a.date}T${a.start_time}`.localeCompare(`${b.date}T${b.start_time}`));

    const cityRows = cities || [];
    const maxCount = Math.max(1, ...cityRows.map((c) => c.count));

    return (
        <div>
            <div className="flex items-start justify-between gap-4">
                <AdminHeader title="Operations Dashboard" subtitle="Live snapshot of the Single Social Club community." />
                <div className="flex shrink-0 flex-col items-end gap-1">
                    <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing} data-testid="dashboard-refresh">
                        <RefreshCw className={`mr-1 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
                    </Button>
                    {refreshedAt && <span className="text-[11px] text-zinc-400">Updated {refreshedAt.toLocaleTimeString()}</span>}
                </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {CARDS.map((c) => (
                    <Link key={c.key} to={c.to} data-testid={`stat-${c.key}`} className="rounded-lg border border-zinc-200 bg-white p-5 transition-colors hover:border-zinc-400">
                        <div className="flex items-center justify-between">
                            <c.icon className={`h-5 w-5 ${c.tone === "red" ? "text-red-500" : c.tone === "amber" ? "text-amber-500" : c.tone === "blue" ? "text-blue-500" : "text-zinc-400"}`} />
                        </div>
                        <p className="mt-3 text-3xl font-700">{data ? data[c.key] : "—"}</p>
                        <p className="text-sm text-zinc-500">{c.label}</p>
                    </Link>
                ))}
            </div>

            <div className="mt-10 grid gap-6 lg:grid-cols-2">
                {/* Upcoming outings */}
                <div data-testid="dashboard-upcoming-outings">
                    <div className="mb-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <CalendarClock className="h-5 w-5 text-zinc-400" />
                            <h2 className="text-lg font-700">Upcoming Group Outings</h2>
                        </div>
                        <Link to="/admin/outings" data-testid="dashboard-view-all-outings" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
                            View all <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                    {upcoming.length === 0 ? (
                        <div className="rounded-lg border border-zinc-200 bg-white p-8 text-center text-sm text-zinc-500" data-testid="dashboard-upcoming-empty">
                            No upcoming outings scheduled right now.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {upcoming.map((o) => (
                                <Link key={o.id} to="/admin/outings" data-testid={`dashboard-outing-${o.id}`}
                                    className="block rounded-lg border border-zinc-200 bg-white p-4 transition-colors hover:border-zinc-400">
                                    <p className="font-600">{o.activity_emoji} {o.activity_name}</p>
                                    <p className="text-sm text-zinc-500">{new Date(o.date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })} · {o.start_time}–{o.end_time} · {o.market}</p>
                                    <p className="mt-1 flex items-center gap-1 text-xs text-zinc-500">
                                        <MapPin className="h-3 w-3" /> {o.venue_name || (o.venue_status === "pending_approval" ? "Venue being finalized" : "No venue set")} · {o.participant_count} members
                                    </p>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>

                {/* City insights */}
                <div data-testid="dashboard-city-insights">
                    <div className="mb-3 flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-zinc-400" />
                        <h2 className="text-lg font-700">Members by City</h2>
                    </div>
                    {cityRows.length === 0 ? (
                        <div className="rounded-lg border border-zinc-200 bg-white p-8 text-center text-sm text-zinc-500" data-testid="dashboard-city-empty">
                            No members yet.
                        </div>
                    ) : (
                        <div className="rounded-lg border border-zinc-200 bg-white p-4">
                            <div className="space-y-2.5">
                                {cityRows.map((c) => (
                                    <Link key={c.city} to={`/admin/members?city=${encodeURIComponent(c.city)}`} data-testid={`city-stat-${c.city.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-center gap-3 rounded-md px-1 py-0.5 transition-colors hover:bg-zinc-50">
                                        <span className="w-32 shrink-0 truncate text-sm text-zinc-700">{c.city}</span>
                                        <div className="h-2.5 flex-1 rounded-full bg-zinc-100">
                                            <div className="h-2.5 rounded-full bg-zinc-900" style={{ width: `${(c.count / maxCount) * 100}%` }} />
                                        </div>
                                        <span className="w-8 shrink-0 text-right text-sm font-600">{c.count}</span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
